import axios from "axios";
import jwtDecode from "jwt-decode";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const SUPERADMIN_TOKEN_KEY = "superAdminAccessToken";
const SUPERADMIN_PROFILE_KEY = "superAdminProfile";

const superadminClient = axios.create({
  baseURL: API_URL,
  withCredentials: false,
});

superadminClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(SUPERADMIN_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function setSuperAdminSession(accessToken, superAdmin) {
  if (!accessToken) return;
  localStorage.setItem(SUPERADMIN_TOKEN_KEY, accessToken);
  if (superAdmin) {
    localStorage.setItem(SUPERADMIN_PROFILE_KEY, JSON.stringify(superAdmin));
  }
}

function clearSuperAdminSession() {
  localStorage.removeItem(SUPERADMIN_TOKEN_KEY);
  localStorage.removeItem(SUPERADMIN_PROFILE_KEY);
}

function getSuperAdminToken() {
  return localStorage.getItem(SUPERADMIN_TOKEN_KEY);
}

function getSuperAdminProfile() {
  const raw = localStorage.getItem(SUPERADMIN_PROFILE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
}

function isSuperAdminTokenValid(token) {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    if (!decoded?.isSuperAdmin) return false;
    if (decoded.exp && Date.now() >= decoded.exp * 1000) return false;
    return true;
  } catch (err) {
    return false;
  }
}

const superadminAPI = {
  login: (email, password) =>
    superadminClient.post("/superadmin/login", { email, password }),
  getOverview: () => superadminClient.get("/superadmin/overview"),
  getTenants: () => superadminClient.get("/superadmin/tenants"),
  getDiscounts: () => superadminClient.get("/superadmin/discounts"),
  saveDiscounts: (discounts, proBasePricePerUserMonth) =>
    superadminClient.put("/superadmin/discounts", {
      discounts,
      proBasePricePerUserMonth,
    }),
  createTenant: (payload) => superadminClient.post("/superadmin/tenants", payload),
  getTenantPricing: (tenantId) =>
    superadminClient.get(`/superadmin/tenants/${tenantId}/pricing`),
  saveTenantPricing: (tenantId, payload) =>
    superadminClient.put(`/superadmin/tenants/${tenantId}/pricing`, payload),
  clearTenantPricing: (tenantId) =>
    superadminClient.delete(`/superadmin/tenants/${tenantId}/pricing`),
  updateTenantBilling: (tenantId, payload) =>
    superadminClient.patch(`/superadmin/tenants/${tenantId}/billing`, payload),
  refundTenant: (tenantId, notes) =>
    superadminClient.post(`/superadmin/tenants/${tenantId}/refund`, { notes }),
  getPaymentHistory: (tenantId) =>
    superadminClient.get(`/superadmin/tenants/${tenantId}/payments`),
  getMessagingSettings: () => superadminClient.get("/superadmin/messaging"),
  setPlatformMessagingProvider: (provider) =>
    superadminClient.put("/superadmin/messaging/default", { provider }),
  setTenantMessagingProvider: (tenantId, provider) =>
    superadminClient.put(`/superadmin/tenants/${tenantId}/messaging`, { provider }),
  getEmailTemplates: () =>
    superadminClient.get("/superadmin/email-templates"),
  saveEmailTemplate: (name, payload) =>
    superadminClient.put(`/superadmin/email-templates/${name}`, payload),
  getBroadcastRecipients: (audience) =>
    superadminClient.get("/superadmin/email-templates/broadcast/recipients", { params: { audience } }),
  sendBroadcast: (payload) =>
    superadminClient.post("/superadmin/email-templates/broadcast/send", payload),
  getAuditLogs: (params) =>
    superadminClient.get("/superadmin/audit-logs", { params }),
  getAuditLogFilters: () =>
    superadminClient.get("/superadmin/audit-logs/filters"),
  exportAuditLogs: (params) =>
    superadminClient.get("/superadmin/audit-logs/export", {
      params,
      responseType: "blob",
    }),
};

export {
  superadminClient,
  superadminAPI,
  setSuperAdminSession,
  clearSuperAdminSession,
  getSuperAdminToken,
  getSuperAdminProfile,
  isSuperAdminTokenValid,
};

